import type { SxProps, Theme } from "@mui/material";

import { isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";
import { theme } from "@/src/theme/theme";
import { heroGradientColors, homePageHeroColors } from "@/src/theme/tokens";

import {
  HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
  HOMEPAGE_SECTION_TITLE_SX
} from "@/src/pages/consumer/HomePage/homePage.constants";

const INSTRUCTIONS_MESH_GRADIENT = [
  `radial-gradient(ellipse 70% 65% at 20% 15%, ${heroGradientColors.base} 0%, transparent 70%)`,
  `radial-gradient(ellipse 60% 60% at 85% 10%, ${homePageHeroColors.meshDeepBrown} 0%, transparent 65%)`,
  `radial-gradient(ellipse 55% 50% at 40% 50%, ${homePageHeroColors.meshMidBrown} 0%, transparent 70%)`,
  `radial-gradient(ellipse 50% 50% at 5% 60%, ${homePageHeroColors.meshTeal} 0%, transparent 65%)`,
  `radial-gradient(ellipse 70% 65% at 75% 90%, ${heroGradientColors.base} 0%, transparent 70%)`
].join(", ");

const instructionsMeshGradientBeforeSx = {
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "-15%",
    backgroundImage: INSTRUCTIONS_MESH_GRADIENT,
    backgroundRepeat: "no-repeat",
    filter: "blur(80px)",
    pointerEvents: "none",
    zIndex: 0
  },
  "& > *": {
    position: "relative",
    zIndex: 1
  }
};

const INSTRUCTIONS_GRADIENT_BACKGROUND = [
  `radial-gradient(ellipse at 85% 15%, ${heroGradientColors.deep} 0%, transparent 45%)`,
  `radial-gradient(ellipse at 10% 60%, ${heroGradientColors.base} 0%, transparent 40%)`,
  `radial-gradient(ellipse at 35% 45%, ${theme.palette.brand.secondary} 0%, transparent 38%)`,
  `radial-gradient(ellipse at 72% 78%, ${heroGradientColors.mid} 0%, transparent 35%)`,
  `radial-gradient(ellipse at 60% 20%, ${heroGradientColors.card} 0%, transparent 35%)`
].join(", ");

export const MOBILE_GRID_ITEM_SX: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "@media (max-width: 699px)": {
    flexBasis: "100%",
    maxWidth: "100%"
  }
};

export const instructionsRootSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  position: "relative",
  overflow: "hidden",
  ...HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
  px: 0,
  py: 0,
  ...(isDarkMode(muiTheme)
    ? {
        backgroundColor: homePageHeroColors.meshBackground,
        ...instructionsMeshGradientBeforeSx
      }
    : {
        backgroundColor: heroGradientColors.mid,
        backgroundImage: INSTRUCTIONS_GRADIENT_BACKGROUND
      })
}));

export const instructionsContainerSx: SxProps<Theme> = {
  ...HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES
};

export const instructionsContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: ["column", "column", "row"],
  backgroundSize: "45% auto",
  backgroundPosition: "right bottom",
  backgroundRepeat: "no-repeat",
  justifyContent: "space-between"
};

export const instructionsTitleSx: SxProps<Theme> = {
  ...HOMEPAGE_SECTION_TITLE_SX,
  color: theme.palette.semantic.primary.contrastText
};

export const instructionsListSx: SxProps<Theme> = {
  ul: {
    listStyle: "none",
    paddingLeft: 0,
    maxWidth: ["auto", "auto", "90%"],
    li: {
      display: "flex",
      flexDirection: ["row-reverse", "row"],
      color: theme.palette.semantic.primary.contrastText,
      alignItems: "center",
      marginBottom: 4,
      whiteSpace: "pre-wrap",
      // The section padding alone sets the bottom gap.
      "&:last-of-type": {
        marginBottom: 0
      }
    }
  }
};

export const instructionsBulletSx: SxProps<Theme> = {
  backgroundRepeat: "no-repeat",
  backgroundSize: "contain",
  backgroundPosition: "center",
  width: "84px",
  height: "89px",
  flexShrink: 0,
  marginRight: [0, 2],
  marginLeft: [2, 0]
};

export const instructionsListItemTextSx: SxProps<Theme> = {
  maxWidth: ["750px", "auto", "100%"]
};

export const instructionsDividerSx: SxProps<Theme> = {
  display: ["none", "none", "flex"],
  border: `1px solid ${theme.palette.action.disabledBackground}`,
  mx: 5
};

export const instructionsDesktopCtaSectionSx: SxProps<Theme> = {
  display: ["none", "none", "flex"],
  flexDirection: "column",
  alignContent: "center",
  justifyContent: "center"
};

export const instructionsDesktopCtaTitleSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    width: ["auto", "auto", "max-content"],
    color: isDarkMode(muiTheme)
      ? muiTheme.palette.text.primary
      : theme.palette.semantic.primary.contrastText,
    ...HOMEPAGE_SECTION_TITLE_SX,
    ml: 1
  })
);

export const instructionsMobileSectionSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    display: ["flex", "flex", "none"],
    position: "relative",
    overflow: "hidden",
    ...(isDarkMode(muiTheme)
      ? {
          backgroundColor: homePageHeroColors.meshBackground,
          ...instructionsMeshGradientBeforeSx
        }
      : {
          backgroundImage: `-webkit-linear-gradient(280deg, ${theme.palette.misc.coldBackground}, ${theme.palette.background.paper})`
        })
  })
);

export const instructionsMobileContentSx: SxProps<Theme> = {
  width: "100%",
  ...HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES
};

export const instructionsMobileTitleSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    ...HOMEPAGE_SECTION_TITLE_SX,
    ...(isDarkMode(muiTheme) && {
      color: muiTheme.palette.text.primary
    })
  })
);
